import type {
  BulkCollectionUpdate,
  CollectionItem,
  CollectionItemInput,
  CollectionListQuery,
  CollectionRow,
  DashboardStats,
  Facets,
} from '@panelvault/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, toQuery } from '../../lib/api';

interface Page<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

export type CollectionParams = Partial<CollectionListQuery>;

export const collectionKeys = {
  list: (params: CollectionParams) => ['collection', 'list', params] as const,
  facets: ['collection', 'facets'] as const,
  stats: ['stats'] as const,
};

export function useCollection(params: CollectionParams) {
  return useQuery({
    queryKey: collectionKeys.list(params),
    queryFn: () => api.get<Page<CollectionRow>>(`/collection${toQuery(params)}`),
    placeholderData: (previous) => previous,
  });
}

export function useFacets() {
  return useQuery({
    queryKey: collectionKeys.facets,
    queryFn: () => api.get<Facets>('/collection/facets'),
    staleTime: 60_000,
  });
}

export function useStats() {
  return useQuery({
    queryKey: collectionKeys.stats,
    queryFn: () => api.get<DashboardStats>('/stats'),
  });
}

function useCollectionInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['collection'] });
    void queryClient.invalidateQueries({ queryKey: ['series'] });
    void queryClient.invalidateQueries({ queryKey: ['stats'] });
  };
}

export function useAddCollectionItem() {
  const invalidate = useCollectionInvalidation();
  return useMutation({
    mutationFn: (input: CollectionItemInput) => api.post<CollectionItem>('/collection', input),
    onSuccess: invalidate,
  });
}

export function useUpdateCollectionItem() {
  const invalidate = useCollectionInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CollectionItemInput> }) =>
      api.patch<CollectionItem>(`/collection/${id}`, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteCollectionItem() {
  const invalidate = useCollectionInvalidation();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/collection/${id}`),
    onSuccess: invalidate,
  });
}

export function useBulkUpdateCollection() {
  const invalidate = useCollectionInvalidation();
  return useMutation({
    mutationFn: (update: BulkCollectionUpdate) =>
      api.post<{ updated: number }>('/collection/bulk', update),
    onSuccess: invalidate,
  });
}
